// Community Features Type Definitions

export interface UserProfile {
  id: string;
  username: string;
  full_name?: string;
  avatar_url?: string;
  bio?: string;
  location?: string;
  pets: string[]; // Array of pet IDs
  reviews_count: number;
  photos_count: number;
  meetups_hosted: number;
  meetups_joined: number;
  created_at: string;
  privacy_settings: {
    show_pets: boolean;
    show_location: boolean;
    show_activity: boolean;
  };
}

export interface ReviewSubmission {
  park_id: string;
  user_id: string;
  pet_id?: string;
  rating: number;
  title?: string;
  content: string;
  visit_date?: string;
  tags?: ReviewTag[];
}

export interface ReviewTag {
  id: string;
  label: string;
  icon?: string;
}

export const REVIEW_TAGS: ReviewTag[] = [
  { id: 'clean', label: 'Clean', icon: '✨' },
  { id: 'safe', label: 'Safe', icon: '🛡️' },
  { id: 'busy', label: 'Busy', icon: '👥' },
  { id: 'quiet', label: 'Quiet', icon: '🤫' },
  { id: 'shady', label: 'Shady', icon: '🌳' },
  { id: 'well-maintained', label: 'Well Maintained', icon: '🔧' },
  { id: 'spacious', label: 'Spacious', icon: '📏' },
  { id: 'friendly-dogs', label: 'Friendly Dogs', icon: '🐕' },
];

export interface PhotoUpload {
  id: string;
  park_id: string;
  user_id: string;
  file: File;
  preview_url: string;
  caption?: string;
  status: 'pending' | 'uploading' | 'approved' | 'rejected';
}

export interface PhotoRecord {
  id: string;
  park_id: string;
  user_id: string;
  username: string;
  url: string;
  caption?: string;
  status: 'pending' | 'approved' | 'rejected';
  uploaded_at: string;
}

export interface HelpfulVote {
  review_id: string;
  user_id: string;
  created_at: string;
}

export interface ContentReport {
  id: string;
  content_type: 'review' | 'photo' | 'message';
  content_id: string;
  reported_by: string;
  reason: ReportReason;
  comment?: string;
  status: 'pending' | 'resolved' | 'dismissed';
  created_at: string;
}

export type ReportReason =
  | 'spam'
  | 'inappropriate'
  | 'harassment'
  | 'misinformation'
  | 'offensive'
  | 'other';

export const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: 'spam', label: 'Spam or advertising' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'misinformation', label: 'False or misleading information' },
  { value: 'offensive', label: 'Offensive language or imagery' },
  { value: 'other', label: 'Other (please specify)' },
];

export interface Meetup {
  id: string;
  park_id: string;
  park_name: string;
  organizer_id: string;
  organizer_name: string;
  organizer_avatar?: string;
  title: string;
  description: string;
  date: string;
  time: string;
  duration_minutes: number;
  max_participants: number;
  current_participants: number;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  participants: MeetupParticipant[];
  created_at: string;
}

export interface MeetupParticipant {
  user_id: string;
  username: string;
  avatar_url?: string;
  pet_name?: string;
  status: 'attending' | 'maybe' | 'declined';
  joined_at: string;
}

export interface WalkingBuddyProfile {
  user_id: string;
  is_active: boolean;
  availability: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  preferred_time: 'morning' | 'afternoon' | 'evening' | 'flexible';
  preferred_parks: string[]; // Park IDs
  pet_size_preference: ('small' | 'medium' | 'large')[];
  pet_energy_preference: ('low' | 'medium' | 'high')[];
  distance_willing: number; // km
  description?: string;
}

export interface WalkingBuddyMatch {
  user_id: string;
  username: string;
  avatar_url?: string;
  pet_name: string;
  pet_size: string;
  pet_energy: string;
  compatibility_score: number;
  common_parks: string[];
  distance_km: number;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  sender_avatar?: string;
  content: string;
  created_at: string;
  read: boolean;
}

export interface Conversation {
  id: string;
  participants: string[]; // User IDs
  participant_names: string[];
  participant_avatars: (string | undefined)[];
  last_message?: string;
  last_message_at?: string;
  unread_count: number;
  created_at: string;
}

export interface NotificationPreferences {
  push_enabled: boolean;
  email_enabled: boolean;
  notifications: {
    messages: boolean;
    meetups: boolean;
    reviews: boolean;
    safety_alerts: boolean;
    walking_buddy: boolean;
    community_updates: boolean;
  };
}

export interface WeatherData {
  temperature: number;
  conditions: string;
  icon: string;
  humidity: number;
  wind_speed: number;
  walking_score: 'excellent' | 'good' | 'fair' | 'poor';
  last_updated: string;
}

export interface AnalyticsEvent {
  event_name: string;
  properties: Record<string, unknown>;
  timestamp: string;
  user_id?: string;
  session_id: string;
}

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  rollout_percentage?: number;
  description?: string;
}
